/**
 * voice-v8 — Wiedergabe-Position merken (Resume), pro Inhalt + version-bewusst.
 *
 * EINE Aufgabe: die letzte Hör-Position in localStorage speichern/laden/löschen.
 * Bei geänderter Inhalts-VERSION (Parameter `version`) wird NICHT fortgesetzt,
 * sondern frisch begonnen — so springt ein neu generierter Text (Biografie-
 * Version, Projekt-Dokument-Version, Essenz/HD-Text-Hash) nie an eine veraltete
 * Stelle. Geteilt von ALLEN Vorlese-Playern (Mein Leben, Projekte, HD, Essenz),
 * damit es nur EINE Resume-Implementierung gibt (DRY).
 *
 * Fail-soft: ist localStorage aus/voll, läuft die Wiedergabe unverändert weiter —
 * Resume ist ein Komfort-Plus, kein Muss.
 */
const PREFIX = "v8-resume:";

interface StoredPos {
  v: string; // Inhalts-Version/Hash zum Zeitpunkt des Speicherns
  pos: number; // Sekunden
}

/** Gespeicherte Position laden — null wenn keine, am Anfang (<=1s), oder die
 *  Inhalts-Version veraltet ist (dann frischer Start). */
export function loadResume(id: string, version: string): number | null {
  try {
    const raw = localStorage.getItem(PREFIX + id);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredPos;
    if (s.v !== version) return null;
    return typeof s.pos === "number" && s.pos > 1 ? s.pos : null;
  } catch {
    return null;
  }
}

/** Position speichern (fail-soft). */
export function saveResume(id: string, version: string, pos: number): void {
  try {
    localStorage.setItem(PREFIX + id, JSON.stringify({ v: version, pos }));
  } catch {
    /* localStorage aus/voll → ignorieren */
  }
}

/** Position löschen (z.B. bei Wiedergabe-Ende → nächstes Mal von vorn). */
export function clearResume(id: string): void {
  try {
    localStorage.removeItem(PREFIX + id);
  } catch {
    /* egal */
  }
}

/** Billiger, SYNCHRONER Inhalts-Stempel (djb2 + Länge) als Versions-Marker für
 *  Texte ohne eigene Versionsnummer (Essenz/HD). Gleicher Text → gleicher Wert. */
export function contentVersion(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36) + ":" + text.length;
}
